/**
 * OCS Recently Viewed
 *
 * Two responsibilities:
 *   1. Save the current PDP product (id + handle) into localStorage['recently_viewed'] on page load.
 *   2. Render a carousel of recently viewed products inside the <ocs-recently-viewed>
 *      custom element by fetching each card via the Section Rendering API
 *      (sections/ocs-rv-card.liquid) and injecting the server-rendered HTML into slides.
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'recently_viewed';

  function getCarouselPageIndex(currentIndex, slideCount, scrollCount) {
    const lastPageIndex = Math.ceil(slideCount / scrollCount) - 1;
    if (currentIndex + scrollCount >= slideCount) return lastPageIndex;
    return Math.max(0, Math.min(Math.floor(currentIndex / scrollCount), lastPageIndex));
  }

  function getWrappedPageTarget(direction, currentPageIndex, slideCount, scrollCount, directionalLoop = false) {
    if (slideCount <= scrollCount) return null;

    const pageCount = Math.ceil(slideCount / scrollCount);
    const pageIndex = Math.max(0, Math.min(currentPageIndex, pageCount - 1));
    if (direction === 'previous') {
      if (pageIndex <= 0) return directionalLoop ? -1 : (pageCount - 1) * scrollCount;
      return (pageIndex - 1) * scrollCount;
    }
    if (direction === 'next') {
      if (pageIndex >= pageCount - 1) return directionalLoop ? slideCount : 0;
      return (pageIndex + 1) * scrollCount;
    }
    return null;
  }

  // ---------------------------------------------------------------------------
  // localStorage helpers
  // ---------------------------------------------------------------------------

  function readStorage() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }

  /** @param {any[]} list */
  function writeStorage(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
      console.warn('[ocs-recently-viewed] Could not write to localStorage.', e);
    }
  }

  // ---------------------------------------------------------------------------
  // Save current product on page load
  // ---------------------------------------------------------------------------

  function readCurrentProduct() {
    const el = document.getElementById('ocs-rv-product-data');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || '');
    } catch (e) {
      console.warn('[ocs-recently-viewed] Could not parse product data JSON.', e);
      return null;
    }
  }

  /** @param {{ id: number, handle: string }} product */
  function saveToStorage(product) {
    let list = readStorage();
    list = list.filter((p) => p && p.id !== product.id);
    list.unshift({ id: product.id, handle: product.handle });
    list = list.slice(0, 13); // keep 12 displayable + 1 to absorb current-product filter
    writeStorage(list);
  }

  // ---------------------------------------------------------------------------
  // Build display list — filter current product by handle, apply limit
  // ---------------------------------------------------------------------------

  /**
   * @param {string} currentHandle
   * @param {number} limit
   * @returns {{ id: number, handle: string }[]}
   */
  function getDisplayList(currentHandle, limit) {
    return readStorage()
      .filter((p) => p && p.handle !== currentHandle)
      .slice(0, limit);
  }

  // ---------------------------------------------------------------------------
  // Section Rendering API fetch
  // ---------------------------------------------------------------------------

  /**
   * Fetches the rendered ocs-product-card HTML for a given product handle
   * via the Section Rendering API endpoint sections/ocs-rv-card.liquid.
   *
   * @param {string} handle
   * @returns {Promise<string|null>}
   */
  async function fetchCardHTML(handle) {
    try {
      const root = ((/** @type {any} */ (window.Shopify))?.routes?.root || '/').replace(/\/$/, '');
      const resp = await fetch(`${root}/products/${encodeURIComponent(handle)}?section_id=ocs-rv-card`);
      if (!resp.ok) return null;
      const html = await resp.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const wrap = doc.querySelector('.ocs-rv-card-wrap');
      // Guard: wrap missing means product doesn't exist (404 fell through)
      if (!wrap) return null;
      // Guard: ocs-product-card breaks early for unavailable products,
      // leaving .ocs-rv-card-wrap in the DOM but empty. Skip those.
      if (!wrap.querySelector('ocs-product-card')) return null;
      return wrap.innerHTML;
    } catch (_) {
      return null;
    }
  }

  // ---------------------------------------------------------------------------
  // Build a slide element from fetched card HTML
  // ---------------------------------------------------------------------------

  /**
   * @param {string} cardHTML
   * @param {number} index
   * @returns {HTMLElement}
   */
  function buildSlide(cardHTML, index) {
    const slide = document.createElement('slideshow-slide');
    slide.setAttribute('ref', 'slides[]');
    slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
    slide.style.setProperty('--slideshow-timeline', `--slide-${index}`);
    slide.style.setProperty('--product-media-fit', 'cover');
    slide.style.setProperty('--grid-template-rows', '50dvh 1fr');
    slide.style.setProperty('--grid-template-rows-desktop', '70dvh 1fr');
    slide.className = 'resource-list__slide';
    slide.innerHTML = cardHTML;
    const card = slide.querySelector('ocs-product-card');
    if (card) card.dataset.ocsItemIndex = String(index + 1);
    return slide;
  }

  // ---------------------------------------------------------------------------
  // Sync --slideshow-timeline on the slideshow-component (mirrors slideshow.liquid)
  // ---------------------------------------------------------------------------

  /**
   * @param {HTMLElement} slideshowComponent
   * @param {number} slideCount
   */
  function updateSlideshowTimeline(slideshowComponent, slideCount) {
    const scopes = [];
    for (let i = 1; i <= slideCount; i++) scopes.push(`--slide-${i}`);
    slideshowComponent.style.setProperty('--slideshow-timeline', scopes.join(','));
  }

  // ---------------------------------------------------------------------------
  // Build slideshow-controls dots element
  // ---------------------------------------------------------------------------

  /**
   * Renders one dot per slide. Visibility of excess dots is managed at runtime
   * by #patchSlideshows() based on the live --column-count CSS variable.
   *
   * @param {number} slideCount
   * @returns {HTMLElement}
   */
  function buildDots(slideCount) {
    const controls = document.createElement('slideshow-controls');
    controls.setAttribute('ref', 'slideshowControls');
    controls.setAttribute('pagination-position', 'center');
    controls.setAttribute('scroll-mode', 'horizontal');
    const ol = document.createElement('ol');
    ol.className = 'slideshow-controls__dots';
    for (let i = 0; i < slideCount; i++) {
      const li = document.createElement('li');
      const btn = document.createElement('button');
      btn.className = 'slideshow-control button button-unstyled';
      btn.setAttribute('aria-label', `Page ${i + 1} of ${slideCount}`);
      // Step is always 1 here; capture-phase click handler in #patchSlideshows
      // overrides this to navigate by live scrollCount × dotIndex instead.
      btn.setAttribute('on:click', `/select/${i}`);
      btn.setAttribute('data-page-dot', '');
      if (i === 0) btn.setAttribute('aria-selected', 'true');
      btn.textContent = String(i + 1);
      li.appendChild(btn);
      ol.appendChild(li);
    }
    controls.appendChild(ol);
    return controls;
  }

  // ---------------------------------------------------------------------------
  // Custom Element
  // ---------------------------------------------------------------------------

  if (!customElements.get('ocs-recently-viewed')) {
    customElements.define(
      'ocs-recently-viewed',
      class extends HTMLElement {
        /** @type {ResizeObserver|null} */
        #resizeObserver = null;
        #rendered = false;

        #patchSlideshows() {
          const baseScrollCount = parseInt(this.dataset.scrollCount || '1', 10);

          this.querySelectorAll('slideshow-component').forEach((slideshow) => {
            const carousel = this.querySelector('.ocs-recently-viewed__carousel');
            // scrollCount is a mutable closure variable — updated by ResizeObserver
            // whenever --column-count changes at a CSS breakpoint.
            let scrollCount = baseScrollCount;

            const totalSlides = () => slideshow.querySelectorAll('[ref="slides[]"]').length;
            const getDots = () => slideshow.querySelectorAll('[data-page-dot]');

            const updateArrows = (currentIndex) => {
              const prev = slideshow.querySelector('[ref="previous"]');
              const next = slideshow.querySelector('[ref="next"]');
              const wraps = slideshow.hasAttribute('infinite');
              if (prev) prev.disabled = !wraps && currentIndex <= 0;
              if (next) next.disabled = !wraps && currentIndex + scrollCount >= totalSlides();
            };

            // Hide dots that represent pages beyond the current page count so the
            // dot row always reflects the live column count, not the server-side value.
            const updateDots = () => {
              const pageCount = Math.ceil(totalSlides() / scrollCount);
              const dots = getDots();
              const dotsContainer = dots[0]?.closest('.slideshow-controls__dots');
              if (dotsContainer) dotsContainer.style.display = pageCount <= 1 ? 'none' : '';
              dots.forEach((dot, i) => {
                const li = dot.closest('li');
                if (li) li.style.display = i < pageCount ? '' : 'none';
              });
            };

            const updateScrollCount = () => {
              if (carousel) {
                const col = parseInt(
                  getComputedStyle(carousel).getPropertyValue('--column-count').trim(),
                  10
                );
                if (!isNaN(col) && col >= 1) scrollCount = col;
              }
              const canWrap = totalSlides() > scrollCount;
              slideshow.toggleAttribute('infinite', canWrap);
              slideshow.toggleAttribute('smooth-loop', canWrap && scrollCount === 1);
              updateDots();
              updateArrows(slideshow.current ?? 0);
            };

            // Own arrow navigation so every click advances exactly one logical page.
            // Horizon handles on:click at the slideshow ancestor during capture, before
            // a listener on the button can cancel it. Keeping the attribute would
            // advance once natively and once here, skipping every other page.
            for (const [selector, direction] of [
              ['[ref="previous"]', 'previous'],
              ['[ref="next"]', 'next'],
            ]) {
              const arrow = slideshow.querySelector(selector);
              arrow?.removeAttribute('on:click');
              arrow?.addEventListener('click', (event) => {
                const selectedPageIndex = Array.from(getDots()).findIndex(
                  (dot) => dot.getAttribute('aria-selected') === 'true'
                );
                const currentPageIndex = selectedPageIndex >= 0
                  ? selectedPageIndex
                  : getCarouselPageIndex(slideshow.current ?? 0, totalSlides(), scrollCount);
                const pageCount = Math.ceil(totalSlides() / scrollCount);
                let targetIndex = null;

                if (slideshow.hasAttribute('infinite')) {
                  targetIndex = getWrappedPageTarget(
                    direction,
                    currentPageIndex,
                    totalSlides(),
                    scrollCount,
                    scrollCount === 1,
                  );
                } else if (direction === 'previous' && currentPageIndex > 0) {
                  targetIndex = (currentPageIndex - 1) * scrollCount;
                } else if (direction === 'next' && currentPageIndex < pageCount - 1) {
                  targetIndex = (currentPageIndex + 1) * scrollCount;
                }

                if (targetIndex === null) return;
                event.preventDefault();
                event.stopImmediatePropagation();
                slideshow.select(targetIndex, event);
              }, { capture: true });
            }

            // Capture-phase click overrides the static on:click="/select/i" attribute
            // so each dot navigates to page i × live scrollCount, not just slide i.
            getDots().forEach((dot, i) => {
              dot.removeAttribute('on:click');
              dot.addEventListener('click', (e) => {
                e.stopImmediatePropagation();
                slideshow.select(i * scrollCount);
              }, { capture: true });
            });

            // Sync dot aria-selected and arrow disabled state on every slide change.
            slideshow.addEventListener('slideshow:select', (e) => {
              const index = /** @type {CustomEvent} */ (e).detail?.index ?? 0;
              const dots = getDots();
              if (dots.length) {
                const pageIndex = getCarouselPageIndex(index, totalSlides(), scrollCount);
                dots.forEach((dot, i) => dot.setAttribute('aria-selected', String(i === pageIndex)));
              }
              updateArrows(index);
            });

            // Watch the carousel element so any CSS breakpoint change to --column-count
            // is picked up and scrollCount / dot visibility / arrow state are updated.
            if (typeof ResizeObserver !== 'undefined' && carousel) {
              new ResizeObserver(updateScrollCount).observe(carousel);
            }

            // Initial sync after layout has settled.
            requestAnimationFrame(updateScrollCount);
          });
        }

        async connectedCallback() {
          if (this.#rendered) return;

          const currentHandle = this.dataset.currentProductHandle || '';
          const limit = parseInt(this.dataset.limit || '12', 10);
          const products = getDisplayList(currentHandle, limit);

          if (products.length === 0) {
            if (!Shopify.designMode) {
              this.style.display = 'none';
            }
            return;
          }

          // Hide while fetching — will be revealed once the carousel is built
          this.style.visibility = 'hidden';

          // Fetch all card HTMLs in parallel via Section Rendering API
          const cardHTMLs = await Promise.all(products.map((p) => fetchCardHTML(p.handle)));

          // Guard: element may have been removed from DOM during async fetch (e.g. navigation)
          if (!this.isConnected) return;

          // Clone the slideshow shell template (arrows + empty slideshow-slides)
          const slideshowTemplate = /** @type {HTMLTemplateElement|null} */ (document.getElementById('ocs-rv-slideshow-template'));
          if (!slideshowTemplate) {
            this.style.display = 'none';
            return;
          }

          const fragment = /** @type {DocumentFragment} */ (slideshowTemplate.content.cloneNode(true));
          const slideshowComponent = /** @type {HTMLElement} */ (fragment.querySelector('slideshow-component'));
          const slideshowSlides = /** @type {HTMLElement} */ (fragment.querySelector('slideshow-slides'));

          // Build slides from fetched HTML — skip any that returned null
          let slideIndex = 0;
          cardHTMLs.forEach((html) => {
            if (!html) return;
            slideshowSlides.appendChild(buildSlide(html, slideIndex++));
          });

          if (slideIndex === 0) {
            this.style.display = 'none';
            return;
          }

          updateSlideshowTimeline(slideshowComponent, slideIndex);
          slideshowComponent.appendChild(buildDots(slideIndex));

          // Insert the complete slideshow (with all slides) into the DOM in one shot.
          // slideshow-component connects exactly once with all slides present — no re-init needed.
          const carouselContainer = this.querySelector('.ocs-recently-viewed__carousel');
          if (!carouselContainer) {
            this.style.display = 'none';
            return;
          }

          carouselContainer.appendChild(fragment);
          this.style.visibility = ''; // reveal

          customElements.whenDefined('slideshow-component').then(() => {
            if (!this.isConnected) return;
            this.#patchSlideshows();
            this.#rendered = true;
          });
        }

        disconnectedCallback() {
          if (this.#resizeObserver) {
            this.#resizeObserver.disconnect();
            this.#resizeObserver = null;
          }
        }
      }
    );
  }

  // Save current product immediately (script is defer, DOM is ready)
  const currentProduct = readCurrentProduct();
  if (currentProduct?.id && currentProduct?.handle) saveToStorage(currentProduct);
})();
